import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { OnchainStateService } from './onchain-state.service';
import { TeamService, MAX_TEAMS, nextFreeSlot } from './team.service';
import { TeamStorageService } from './team-storage.service';

export interface TeamSummary {
  slot: number;
  name: string;
  monIds: readonly bigint[];
  isDefault: boolean;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class TeamListService {
  private onchain = inject(OnchainStateService);
  private team = inject(TeamService);
  private storage = inject(TeamStorageService);

  private _names = signal<Record<number, string>>({});
  private _defaultIndex = signal<number>(0);
  private _draftSlot = signal<number | null>(null);

  readonly defaultIndex = this._defaultIndex.asReadonly();
  readonly draftSlot    = this._draftSlot.asReadonly();
  readonly activeIndex  = this.onchain.activeTeamIndex;

  readonly teams = computed<TeamSummary[]>(() => {
    const slots = this.onchain.orderedSlots();
    const map = this.onchain.monIndicesByTeam();
    const def = this._defaultIndex();
    const active = this.activeIndex();
    return slots.map(slot => ({
      slot,
      name: this.nameForSlot(slot),
      monIds: map.get(slot) ?? [],
      isDefault: slot === def,
      isActive: slot === active,
    }));
  });

  readonly activeTeam = computed<TeamSummary | null>(
    () => this.teams().find((t) => t.isActive) ?? null,
  );

  nameForSlot(slot: number): string {
    return this._names()[slot] ?? `Team ${slot + 1}`;
  }

  // Tracks the address we've already applied the stored-default snap-to
  // for. Re-running on every orderedSlots change would overwrite the
  // user's selection after creating a team (appendCreatedTeam → effect
  // → setActiveTeamIndex(storedDefault) → bounce off the new team).
  private appliedDefaultForAddr: string | null = null;

  constructor() {
    effect(() => {
      const addr = this.onchain.activeAddress();
      if (!addr) {
        this._names.set({});
        this._defaultIndex.set(0);
        this._draftSlot.set(null);
        this.appliedDefaultForAddr = null;
        return;
      }
      this._names.set(this.storage.readNames(addr));
      const storedDefault = this.storage.readDefault(addr);
      this._defaultIndex.set(storedDefault);

      const slots = this.onchain.orderedSlots();
      if (
        this.appliedDefaultForAddr !== addr &&
        slots.length > 0 &&
        slots.includes(storedDefault)
      ) {
        this.onchain.setActiveTeamIndex(storedDefault);
        this.appliedDefaultForAddr = addr;
      }
    });
  }

  selectTeam(slot: number): void {
    this.onchain.setActiveTeamIndex(slot);
  }

  renameTeam(slot: number, name: string): void {
    const addr = this.onchain.activeAddress();
    if (!addr) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    this.storage.setName(addr, slot, trimmed);
    this._names.set({ ...this._names(), [slot]: trimmed });
  }

  setDefault(slot: number): void {
    const addr = this.onchain.activeAddress();
    if (!addr) return;
    this.storage.writeDefault(addr, slot);
    this._defaultIndex.set(slot);
  }

  beginDraft(): void {
    if (this.teams().length >= MAX_TEAMS) return;
    if (this._draftSlot() !== null) return;
    const slot = nextFreeSlot(this.onchain.liveBitmap());
    this._draftSlot.set(slot);
  }

  cancelDraft(): void {
    this._draftSlot.set(null);
  }

  async removeTeam(slot: number): Promise<void> {
    await this.team.deleteTeam(BigInt(slot));
    const addr = this.onchain.activeAddress();
    if (addr) this.storage.removeName(addr, slot);
    const { [slot]: _, ...rest } = this._names();
    this._names.set(rest);

    const slots = this.onchain.orderedSlots();
    const def = this._defaultIndex();
    if (this.activeIndex() === slot) {
      const fallback = slots.includes(def) ? def : (slots[0] ?? 0);
      this.onchain.setActiveTeamIndex(fallback);
    }
    if (def === slot) {
      this.setDefault(slots[0] ?? 0);
    }
  }
}
